<template>
  <div class="yhc-service-level-notice">
    <div class="yhc-service-level-notice__header">お知らせ</div>
    <div class="yhc-service-level-notice__list">
      <div
        v-for="(notice, index) in sanitizedNotices"
        :key="notice.id || index"
        class="yhc-service-level-notice__item"
        :class="{ 'yhc-service-level-notice__item--new': notice.is_new }"
      >
        <div class="yhc-service-level-notice__date-wrapper">
          <div class="yhc-service-level-notice__date">
            {{ notice.displayDateFormatted }}
          </div>
          <Badge
            v-if="notice.is_new"
            variant="primary"
            class="yhc-service-level-notice__new-badge"
            >新着</Badge
          >
        </div>
        <div
          class="yhc-service-level-notice__content"
          v-html="notice.contentHtml"
          @click="handleTooltipToggle($event, notice)"
        ></div>
        <ul
          v-if="notice.attachmentLinks.length"
          class="yhc-service-level-notice__attachments"
        >
          <li
            v-for="link in notice.attachmentLinks"
            :key="link.href"
          >
            <a
              :href="link.href"
              target="_blank"
              rel="noopener noreferrer"
              :download="link.downloadName"
              >{{ link.label }}</a
            >
          </li>
        </ul>
      </div>
    </div>
    <!-- リッチテキスト対応ツールチップ -->
    <teleport to="body">
      <div
        v-if="activeTooltip"
        class="notice-richtext-tooltip"
        :class="[
          `notice-richtext-tooltip--${tooltipPosition.placement}`,
          `notice-richtext-tooltip--${tooltipPosition.horizontalAlign}`,
        ]"
        :style="{ top: tooltipPosition.top, left: tooltipPosition.left }"
        v-html="activeTooltip.content"
      ></div>
    </teleport>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import sanitizeHtml from 'sanitize-html'
import Badge from './ui/Badge.vue'
import type { FindFilter } from '../../../../CORE/src/common/$models'
import {
  findRecordsByModelName,
  SERVICE_LEVEL_NOTICE_HIDDEN_PAGES_DIRECTUS_HINT,
} from '../../YhcEstimateFormFrontPage/findRecordsByModelName'
import type { ServiceLevelNoticePageKey } from '../serviceLevelNoticePageUtils'
import {
  filterNoticesVisibleOnPage,
  formatServiceLevelNoticeForDisplay,
  type FormattedServiceLevelNotice,
  type ServiceLevelNoticeRecordForDisplay,
} from '../serviceLevelNoticeDisplayUtils'

type SanitizeHtmlOptions = NonNullable<Parameters<typeof sanitizeHtml>[1]>

type ServiceLevelNoticeRecord = ServiceLevelNoticeRecordForDisplay & {
  title: string | null
  enabled_from: string | null
  disabled_at: string | null
  modal_start_datetime: string | null
  modal_end_datetime: string | null
  is_new: boolean
}

type SanitizedNotice = ServiceLevelNoticeRecord & FormattedServiceLevelNotice

export const sanitizeOptions: SanitizeHtmlOptions = {
  allowedTags: ['a', 'b', 'br', 'em', 'i', 'p', 'span', 'strong', 'u', 'ul', 'ol', 'li'],
  allowedAttributes: {
    a: ['href', 'target', 'rel', 'style'],
    b: ['style'],
    em: ['style'],
    i: ['style'],
    p: ['style'],
    span: ['class', 'style'],
    strong: ['style'],
    u: ['style'],
    ul: ['style'],
    ol: ['style'],
    li: ['style'],
  },
  allowedStyles: {
    '*': {
      color: [/.*/],
      'background-color': [/.*/],
      background: [/.*/],
    },
  },
  transformTags: {
    a: sanitizeHtml.simpleTransform('a', {
      target: '_blank',
      rel: 'noopener noreferrer',
    }),
  },
}

export default defineComponent({
  name: 'YhcServiceLevelNotice',
  components: {
    Badge,
  },
  props: {
    noticePageKey: {
      type: String as PropType<ServiceLevelNoticePageKey>,
      required: true,
    },
  },
  data() {
    return {
      notices: [] as ServiceLevelNoticeRecord[],
      activeTooltip: null as { content: string; noticeId: string } | null,
      tooltipPosition: {
        top: '0px',
        left: '0px',
        placement: 'top' as 'top' | 'bottom',
        horizontalAlign: 'center' as 'center' | 'left' | 'right',
      },
    }
  },
  computed: {
    sanitizedNotices(): SanitizedNotice[] {
      return this.notices.map((notice) => ({
        ...notice,
        ...formatServiceLevelNoticeForDisplay(notice, sanitizeOptions),
      }))
    },
  },
  watch: {
    noticePageKey() {
      void this.fetchNotices()
    },
  },
  async mounted() {
    await this.fetchNotices()
    document.addEventListener('click', this.handleOutsideClick)
    document.addEventListener('keydown', this.handleKeyDown)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('keydown', this.handleKeyDown)
  },
  methods: {
    async fetchNotices() {
      try {
        const now = $core.$dayjs().format('YYYY-MM-DD HH:mm:ss')
        const filterAnd: FindFilter[] = [
          {
            _or: [{ enabled_from: { _lte: now } }, { enabled_from: { _null: true } }],
          },
          {
            _or: [{ disabled_at: { _gte: now } }, { disabled_at: { _null: true } }],
          },
        ]
        const records = await findRecordsByModelName('yhc_service_level_notices', {
          filter: {
            _and: filterAnd,
          },
          sort: ['-display_date'],
        })
        this.notices = filterNoticesVisibleOnPage(
          records as ServiceLevelNoticeRecord[],
          this.noticePageKey,
        )
      } catch (error) {
        console.error('お知らせの取得に失敗しました:', error)
        console.error(SERVICE_LEVEL_NOTICE_HIDDEN_PAGES_DIRECTUS_HINT)
        this.notices = []
      }
    },
    handleTooltipToggle(event: MouseEvent, notice: SanitizedNotice) {
      const target = event.target as HTMLElement
      if (
        !target.classList.contains('notice-tooltip-trigger') ||
        !notice.tooltipContentHtml
      ) {
        return
      }

      event.stopPropagation()

      if (this.activeTooltip && this.activeTooltip.noticeId === notice.id) {
        this.activeTooltip = null
        return
      }

      const rect = target.getBoundingClientRect()
      const tooltipMaxHeight = 200
      const tooltipWidth = 300
      const margin = 20

      const spaceAbove = rect.top
      const spaceBelow = window.innerHeight - rect.bottom
      const placement: 'top' | 'bottom' =
        spaceAbove < tooltipMaxHeight + margin && spaceBelow > spaceAbove
          ? 'bottom'
          : 'top'

      const top =
        placement === 'top'
          ? rect.top + window.scrollY - 10
          : rect.bottom + window.scrollY + 10

      const triggerCenterX = rect.left + rect.width / 2
      const halfTooltipWidth = tooltipWidth / 2
      let horizontalAlign: 'center' | 'left' | 'right' = 'center'
      let left = triggerCenterX + window.scrollX

      if (triggerCenterX - halfTooltipWidth < margin) {
        horizontalAlign = 'left'
        left = Math.max(margin, rect.left) + window.scrollX
      } else if (triggerCenterX + halfTooltipWidth > window.innerWidth - margin) {
        horizontalAlign = 'right'
        left = Math.min(window.innerWidth - margin, rect.right) + window.scrollX
      }

      this.tooltipPosition = {
        top: `${top}px`,
        left: `${left}px`,
        placement,
        horizontalAlign,
      }
      this.activeTooltip = {
        content: notice.tooltipContentHtml,
        noticeId: notice.id,
      }
    },
    handleOutsideClick(event: MouseEvent) {
      const target = event.target as HTMLElement
      if (target.closest('.notice-richtext-tooltip')) {
        return
      }
      if (target.classList.contains('notice-tooltip-trigger')) {
        return
      }
      this.activeTooltip = null
    },
    handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && this.activeTooltip) {
        this.activeTooltip = null
      }
    },
  },
})
</script>

<style lang="scss">
// ツールチップトリガー（対象テキスト）- aタグのような青字下線スタイル
.notice-tooltip-trigger {
  color: #0066cc;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: #004499;
  }
}

// リッチテキスト対応ツールチップ（teleportでbodyに配置されるため、scopedではない）
.notice-richtext-tooltip {
  position: absolute;
  z-index: 10000;
  background: #333;
  color: white;
  padding: 0.75rem;
  border-radius: 6px;
  max-width: 300px;
  max-height: 200px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.5;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  ul,
  ol {
    margin: 0.25rem 0;
    padding-left: 1.25em;
    color: inherit;
  }

  li {
    margin-bottom: 0.2rem;
    color: inherit;
  }

  p {
    margin: 0 0 0.25rem 0;
    color: inherit;
  }

  span,
  strong,
  b,
  em,
  i,
  u {
    color: inherit;
  }

  [style*='color'] {
    color: unset;
  }

  a {
    color: #7dd3fc;
  }

  strong,
  b {
    font-weight: 600;
  }

  &::after {
    content: '';
    position: absolute;
    border: 8px solid transparent;
  }

  &--top {
    &::after {
      top: 100%;
      border-top-color: #333;
      border-bottom-color: transparent;
    }
  }

  &--bottom {
    &::after {
      bottom: 100%;
      top: auto;
      border-bottom-color: #333;
      border-top-color: transparent;
    }
  }

  &--center {
    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }

  &--left {
    &::after {
      left: 20px;
      transform: none;
    }
  }

  &--right {
    &::after {
      right: 20px;
      left: auto;
      transform: none;
    }
  }

  &--top.notice-richtext-tooltip--center {
    transform: translate(-50%, -100%);
  }

  &--top.notice-richtext-tooltip--left {
    transform: translate(0, -100%);
  }

  &--top.notice-richtext-tooltip--right {
    transform: translate(-100%, -100%);
  }

  &--bottom.notice-richtext-tooltip--center {
    transform: translate(-50%, 0);
  }

  &--bottom.notice-richtext-tooltip--left {
    transform: translate(0, 0);
  }

  &--bottom.notice-richtext-tooltip--right {
    transform: translate(-100%, 0);
  }
}
</style>

<style lang="scss" scoped>
.yhc-service-level-notice {
  width: 100%;
  background-color: #fff;
  border-radius: 6px;
  box-shadow: 1px 1px 8px rgba(0, 0, 0, 0.15);
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  max-height: 180px;
  box-sizing: border-box;

  &__header {
    font-size: 16px;
    font-weight: 600;
    color: var(--yhc-color);
    margin-bottom: 10px;
    border-bottom: 2px solid var(--yhc-color);
  }

  &__list {
    overflow-y: auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__item {
    padding: 3px 10px;
    background-color: #f8f9fa;
    border-radius: 4px;
    border-left: 3px solid var(--yhc-color);
  }

  &__date-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 2px;
  }

  &__date {
    font-size: 12px;
    color: #666;
    font-weight: 500;
  }

  &__new-badge {
    font-size: 0.7rem;
    padding: 0.15rem 0.4rem;
  }

  &__content {
    font-size: 14px;
    color: #333;
    line-height: 1.4;

    :deep(ul) {
      margin: 0;
      padding-left: 1em;
      list-style-type: circle;
    }

    :deep(li) {
      font-size: 13px;
      line-height: 1.3;
    }
  }

  &__attachments {
    margin: 4px 0 0;
    padding-left: 1.2em;
    list-style-type: disc;

    li {
      font-size: 13px;
      line-height: 1.3;
    }

    a {
      color: #0066cc;
      text-decoration: underline;

      &:hover {
        color: #004499;
      }
    }
  }

  &__item--new &__content {
    color: #dc3545;

    :deep(li) {
      color: #dc3545;
    }
  }

  @media (max-width: 767px) {
    max-height: 150px;
    padding: 10px 12px;

    &__header {
      font-size: 14px;
      margin-bottom: 10px;
    }

    &__list {
      gap: 8px;
    }

    &__item {
      padding: 3px 8px;
    }

    &__date-wrapper {
      gap: 0.3rem;
    }

    &__date {
      font-size: 11px;
    }

    &__new-badge {
      font-size: 0.65rem;
      padding: 0.1rem 0.3rem;
    }

    &__content {
      font-size: 13px;
      line-height: 1.3;
    }

    &__attachments li {
      font-size: 12px;
    }
  }
}
</style>
